'use client';

import { ReactNode } from 'react';

import { CardAppointmentHeader, CardAppointmentHeaderProps } from './card-appointment-header.component';
import { DetailsAccordion } from './details-accordion.component';
import { GuideCodeField } from './guide-code-field.component';
import { LabeledField } from './labeled-field.component';
import { LocationBlock } from './location-block.component';

import {
  AppointmentReviewExpiredBanner,
  AppointmentReviewPendingBanner,
} from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-review/appointment-review-banners.component';
import { AppointmentReviewButton } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-review/appointment-review-button.component';
import { AppointmentReviewSummary } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-review/appointment-review-summary.component';
import { Card } from '@/components/ui/card.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useAppointmentReviewStatus } from '@/hooks/fetch/appointment/use-appointment-review-status.hook';
import { Appointment } from '@/types/appointments.types';

interface CardAppointmentRealizedLocation {
  socialName: string;
  href?: string;
  address: string;
  phone?: string;
  email?: string;
  phonePrefix?: string;
}

interface CardAppointmentRealizedProps {
  testId: string;
  defaultOpen?: boolean;
  entity: CardAppointmentHeaderProps;
  procedureName?: string;
  formattedDate?: string;
  patientName: string;
  appointment: Appointment;
  location?: CardAppointmentRealizedLocation;
  preAccordionExtras?: ReactNode;
  expandedContent: ReactNode;
  reviewStatus: ReturnType<typeof useAppointmentReviewStatus>;
}

export function CardAppointmentRealized({
  testId,
  defaultOpen,
  entity,
  procedureName,
  formattedDate,
  patientName,
  appointment,
  location,
  preAccordionExtras,
  expandedContent,
  reviewStatus,
}: CardAppointmentRealizedProps) {
  const {
    rating,
    isAppointmentRatable,
    isLoadingPartialReview,
    isVisiblePendingReviewBanner,
    isVisibleExpiredReviewBanner,
  } = reviewStatus;

  return (
    <Card data-testid={testId} className="flex flex-col gap-4 rounded-2xl p-4 w-full">
      <div className="flex flex-col gap-4">
        <CardAppointmentHeader {...entity} />

        {procedureName && (
          <Typography variant={TypographyVariant.BODY_TEXT} className="font-normal">
            {procedureName}
          </Typography>
        )}

        {preAccordionExtras}

        {formattedDate && (
          <LabeledField label="Data e hora">
            <Typography>{formattedDate}</Typography>
          </LabeledField>
        )}

        {rating
          ? <AppointmentReviewSummary id={appointment.id} rating={rating} />
          : (isAppointmentRatable || isLoadingPartialReview) && (
              <AppointmentReviewButton id={appointment.id} isLoading={isLoadingPartialReview} />
            )}

        {!rating && isVisiblePendingReviewBanner && <AppointmentReviewPendingBanner />}
        {!rating && isVisibleExpiredReviewBanner && <AppointmentReviewExpiredBanner />}
      </div>

      <DetailsAccordion defaultOpen={defaultOpen}>
        <div className="flex flex-col gap-4">
          <hr className="border-t border-gray-200 w-full" />

          {appointment.referral && <GuideCodeField appointment={appointment} />}

          <LabeledField label="Atendimento para">
            <Typography>{patientName}</Typography>
          </LabeledField>

          {location && (
            <LocationBlock
              socialName={location.socialName}
              href={location.href}
              address={location.address}
              phone={location.phone}
              email={location.email}
              phonePrefix={location.phonePrefix}
            />
          )}

          {expandedContent}
        </div>
      </DetailsAccordion>
    </Card>
  );
}
